import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MessageCircle, CalendarClock, ChevronDown } from "lucide-react";
import NeuralCanvas from "@/components/NeuralCanvas";
import { useLanguage } from "@/i18n/LanguageContext";

const HERO_VIDEO = "/hero-neural.mp4";

const wordVariants = {
  hidden: { y: 24, opacity: 0 },
  visible: (i) => ({
    y: 0,
    opacity: 1,
    transition: { delay: 0.15 + i * 0.07, duration: 0.5, ease: "easeOut" },
  }),
};

const Hero = ({ onOpenDemo, waLink }) => {
  const { t } = useLanguage();
  const [videoOk, setVideoOk] = useState(true);
  const [lightHero, setLightHero] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setLightHero(mq.matches || !!navigator.connection?.saveData);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  let idx = 0;
  const renderWords = (words, className) =>
    words.map((w) => {
      const i = idx++;
      return (
        <motion.span
          key={`${w}-${i}`}
          custom={i}
          variants={wordVariants}
          initial="hidden"
          animate="visible"
          className={`inline-block mr-[0.28em] ${className}`}
          data-text={w}
        >
          {w}{" "}
        </motion.span>
      );
    });

  return (
    <section className="scanlines relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      {lightHero ? (
        <img
          src="/hero-neural-poster.webp"
          alt=""
          aria-hidden="true"
          data-testid="hero-video-poster"
          className="absolute inset-0 w-full h-full object-cover opacity-45 pointer-events-none"
          style={{
            maskImage: "linear-gradient(to right, transparent 0%, black 38%, black 100%)",
            WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 38%, black 100%)",
          }}
        />
      ) : (
        videoOk && (
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster="/hero-neural-poster.webp"
            data-testid="hero-video"
            className="absolute inset-0 w-full h-full object-cover opacity-45 md:opacity-55 pointer-events-none"
            style={{
              maskImage: "linear-gradient(to right, transparent 0%, black 38%, black 100%)",
              WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 38%, black 100%)",
            }}
          >
            <source src="/hero-neural.webm" type="video/webm" />
            <source src="/hero-neural.mp4" type="video/mp4" onError={() => setVideoOk(false)} />
          </video>
        )
      )}
      <NeuralCanvas className={`absolute inset-0 w-full h-full pointer-events-none transition-opacity duration-700 ${videoOk || lightHero ? "opacity-40 md:opacity-70" : "opacity-70 md:opacity-100"}`} />
      <div className="absolute inset-0 cyber-grid pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_50%,rgba(3,3,5,0.9),transparent_60%)] pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-t from-void via-transparent to-void/60 pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-32 pb-24 w-full">
        <div className="max-w-2xl">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="hud-label text-xs uppercase text-neon-cyan mb-6"
            data-testid="hero-overline"
          >
            {t.hero.overline}
          </motion.p>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase leading-[1.05]" data-testid="hero-headline">
            <span className="sr-only">{t.hero.h1Plain}</span>
            <span aria-hidden="true">
              {renderWords(t.hero.h1a, "")}
              {renderWords(t.hero.h1neon, "neon-text neon-glow glitch")}
              {renderWords(t.hero.h1b, "")}
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.5 }}
            className="mt-6 text-base lg:text-lg leading-relaxed text-white/70 max-w-xl"
            data-testid="hero-subtitle"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.5 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              href={waLink(t.hero.whatsappMessage)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              className="flex items-center gap-2 btn-cyber bg-gradient-to-r from-neon-cyan to-neon-violet px-7 py-4 font-semibold text-void hover:opacity-90 hover:-translate-y-0.5 transition-all duration-300"
            >
              <MessageCircle size={18} /> {t.hero.whatsapp}
            </a>
            <button
              onClick={onOpenDemo}
              data-testid="hero-demo-button"
              className="flex items-center gap-2 btn-cyber border border-white/20 px-7 py-4 font-semibold text-white hover:border-neon-cyan/60 hover:text-neon-cyan hover:-translate-y-0.5 transition-all duration-300"
            >
              <CalendarClock size={18} /> {t.hero.demo}
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.35, duration: 0.6 }}
            className="mt-8 text-sm text-white/50 flex items-center gap-2"
            data-testid="hero-claim"
          >
            <span className="w-2 h-2 rounded-full bg-neon-cyan animate-pulse inline-block" />
            {t.hero.claim}
          </motion.p>
        </div>
      </div>

      <motion.a
        href="#servizi"
        aria-label="Scorri ai servizi"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/40 hover:text-neon-cyan transition-colors p-3 -m-1"
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
      >
        <ChevronDown size={28} />
      </motion.a>
    </section>
  );
};

export default Hero;
